<template>
  <LoginLayout>
    <RoutineLogoIcon :is-colored="true" class="logo" />
    <h1>{{ signText }}</h1>
    <h2>{{ signText }} with email, Google, Microsoft or Apple.</h2>
    <div class="buttons">
      <BaseButton
        kind="important"
        type="secondary"
        :text="`${signText} with Google`"
        :state="googleFlowStarted ? 'disabled' : 'default'"
        true-colors-icon
        @click="handleGoogleFlow"
      >
        <template #icon>
          <GoogleIcon />
        </template>
      </BaseButton>

      <div class="email-signin">
        <div class="divider">
          <span>or</span>
        </div>
        <BaseInput
          ref="emailInputRef"
          :value="email"
          size="xl"
          placeholder="Enter your email"
          :is-disabled="emailFlowStarted"
          has-styles
          display="form"
          @update:value="email = $event"
          @enter="handleEmailFlow"
        />
        <BaseButton
          kind="important"
          type="secondary"
          text="Continue with email"
          :state="emailFlowStarted ? 'disabled' : 'default'"
          @click="handleEmailFlow"
        />
      </div>
    </div>
    <p>
      By signing {{ mode === "signup" ? "up" : "in" }}, you agree to the
      <BaseLink size="small" @click="openUrl('https://routine.co/terms')">
        Terms and Conditions </BaseLink
      ><br />
      and to receive emails related to the service.
    </p>
    <p v-if="showBack" class="back">
      <BaseLink
        size="small"
        color="grey"
        :pointer="false"
        @click="$emit('prevStep')"
      >
        ← Back
      </BaseLink>
    </p>
  </LoginLayout>
</template>

<script lang="ts">
import { defineComponent, onMounted, PropType, ref } from "vue";

import GoogleIcon from "@/components/icons/apps/google/GoogleIcon.vue";
import { metrics } from "@/metrics";
import { openExternalUrl } from "@/utils/open-external-url";

import BaseButton from "../base/BaseButton.vue";
import BaseInput from "../base/BaseInput.vue";
import BaseLink from "../base/BaseLink.vue";
import RoutineLogoIcon from "../icons/RoutineLogoIcon.vue";
import LoginLayout from "./LoginLayout.vue";

export default defineComponent({
  components: {
    BaseButton,
    BaseInput,
    BaseLink,
    GoogleIcon,
    LoginLayout,
    RoutineLogoIcon,
  },

  props: {
    googleFlowStarted: { type: Boolean, required: true },
    emailFlowStarted: { type: Boolean, required: true },
    showBack: { type: Boolean, default: true },
    mode: { type: String as PropType<"signin" | "signup">, default: "signin" },
  },

  emits: {
    prevStep: () => true,
    startGoogleFlow: () => true,
    startEmailFlow: (_email: string) => true,
  },

  setup(props, { emit }) {
    const signText = props.mode === "signup" ? "Sign up" : "Sign in";
    const openUrl = (url: string) => openExternalUrl(url, null, false);
    const email = ref("");
    const emailInputRef = ref<InstanceType<typeof BaseInput> | null>(null);

    onMounted(() => {
      emailInputRef.value?.focus();
    });

    const handleGoogleFlow = () => {
      metrics.segment.page("Onboarding", {
        step: "authenticating",
        provider: "google",
      });
      emit("startGoogleFlow");
    };

    const handleEmailFlow = () => {
      const trimmedEmail = email.value.trim();
      if (!trimmedEmail || !trimmedEmail.includes("@")) {
        // TODO: Show validation error
        return;
      }
      metrics.segment.page("Onboarding", {
        step: "authenticating",
        provider: "email",
      });
      emit("startEmailFlow", trimmedEmail);
    };

    metrics.segment.page("Onboarding", { step: "authentication" });

    return {
      email,
      emailInputRef,
      handleEmailFlow,
      handleGoogleFlow,
      openUrl,
      signText,
    };
  },
});
</script>

<style lang="scss" scoped>
.logo {
  margin-top: 32px;
}

h2 {
  margin-bottom: 64px;
}

.buttons {
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  margin-bottom: 32px;

  button {
    width: 100%;
  }

  :deep(button .left) {
    width: 100%;
    justify-content: center;
  }

  :deep(.base-input .input) {
    text-align: center;
  }
}

p {
  @extend %font-small-regular;
  max-width: 350px;
  @include themed() {
    color: t($grey-50);
  }
}

.email-signin {
  width: 222px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .divider {
    text-align: center;
    margin: 8px 0;
    position: relative;

    &::before,
    &::after {
      content: "";
      position: absolute;
      top: 50%;
      width: 40%;
      height: 1px;
      @include themed() {
        background: t($grey-15);
      }
    }

    &::before {
      inset-inline-start: 0;
    }
    &::after {
      inset-inline-end: 0;
    }

    span {
      @extend %font-small-regular;
      @include themed() {
        color: t($grey-50);
      }
    }
  }
}
</style>
